export const AFFILIATE_REL = 'nofollow sponsored'

/** Tracking domains and shorteners used by TIGG affiliate programs. */
const AFFILIATE_NETWORK =
  /skimresources|go\.skim|amzn\.to|amazon\.(?:com|co\.uk|ca|de|fr|it|es)\/|shareasale|pxf\.io|impact\.com|evyy\.net|sjv\.io|awin1\.com|linksynergy|rakuten|rewardstyle|rstyle\.me|ltk\.app|shopstyle|fave\.co|howl\.me|anrdoezrs|dpbolvw|jdoqocy|tkqlhce|qksrv\.net|pjtra\.com|pntra\.com|pepperjam|avantlink|flexoffers|partnerize|shopmy\.us|collabs\.shop|sovrn\.com|redirectingat\.com|ebay\.(?:com|co\.uk)\/itm/i

const SKIP_HOSTS = [
  'theitgirlguide.com',
  'instagram.com',
  'twitter.com',
  'x.com',
  't.co',
  'pinterest.com',
  'tiktok.com',
  'facebook.com',
  'fb.com',
  'youtube.com',
  'youtu.be',
  'spotify.com',
  'open.spotify.com',
  'music.apple.com',
  'substack.com',
  'substackcdn.com',
  'vogue.com',
  'elle.com',
  'elledecor.com',
  'harpersbazaar.com',
  'wwd.com',
  'businessoffashion.com',
  'nytimes.com',
  'nymag.com',
  'thecut.com',
  'bhg.com',
  'housebeautiful.com',
  'countryliving.com',
  'realsimple.com',
  'thespruce.com',
  'apartmenttherapy.com',
  'travelandleisure.com',
  'nationalgeographic.com',
  'independent.co.uk',
  'redonline.co.uk',
  'femestella.com',
  'timesofindia.indiatimes.com',
  'webmd.com',
  'mayoclinic.org',
  'nhs.uk',
  'gov.uk',
  'apa.org',
  'nih.gov',
  'wikipedia.org',
  'pexels.com',
]

function hostnameOf(href: string) {
  try {
    const raw = href.replace(/&amp;/g, '&').trim()
    if (!raw || /^(#|mailto:|tel:)/i.test(raw)) return null
    if (raw.startsWith('/') && !raw.startsWith('//')) return 'theitgirlguide.com'
    const url = new URL(raw, 'https://theitgirlguide.com')
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
    return url.hostname.replace(/^www\./i, '').toLowerCase()
  } catch {
    return null
  }
}

function isSkippedHost(host: string) {
  if (/\.(?:gov|edu|ac\.uk|gov\.uk)$/i.test(host)) return true
  return SKIP_HOSTS.some((skip) => host === skip || host.endsWith(`.${skip}`))
}

export function isAffiliateHref(href: string) {
  if (!href) return false
  if (AFFILIATE_NETWORK.test(href)) return true
  const host = hostnameOf(href)
  if (!host || isSkippedHost(host)) return false
  const raw = href.trim()
  if (raw.startsWith('/') && !raw.startsWith('//')) return false
  if (!/^https?:/i.test(raw) && !raw.startsWith('//')) return false
  return true
}

export function affiliateRelForHref(href: string) {
  return isAffiliateHref(href) ? AFFILIATE_REL : undefined
}

const STRIP_PARAMS = new Set([
  'gclid',
  'gbraid',
  'wbraid',
  'dclid',
  'fbclid',
  'msclkid',
  'gclsrc',
  'gad_source',
  'gad_campaignid',
  '_gl',
])

const NESTED_DEST = new Set(['url', 'murl'])

function shouldStripParam(key: string) {
  const name = key.toLowerCase()
  return name.startsWith('utm_') || STRIP_PARAMS.has(name)
}

function stripTrackingUrl(href: string): string {
  let url: URL
  try {
    url = new URL(href)
  } catch {
    return href
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return href

  let changed = false
  const kept: [string, string][] = []
  for (const [key, value] of url.searchParams) {
    if (shouldStripParam(key)) {
      changed = true
      continue
    }
    if (NESTED_DEST.has(key.toLowerCase()) && /^https?:\/\//i.test(value)) {
      const cleaned = stripTrackingUrl(value)
      if (cleaned !== value) {
        changed = true
        kept.push([key, cleaned])
        continue
      }
    }
    kept.push([key, value])
  }
  if (!changed) return href

  const query = kept.length
    ? `?${kept.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join('&')}`
    : ''
  const slashBeforeQuery = url.pathname !== '/' || /^https?:\/\/[^/?#]+\//i.test(href)
  const pathname = url.pathname === '/' && !slashBeforeQuery ? '' : url.pathname
  return `${url.origin}${pathname}${query}${url.hash}`
}

function isInternalHref(href: string) {
  if (!href || /^(#|mailto:|tel:|javascript:)/i.test(href)) return true
  if (href.startsWith('/') && !href.startsWith('//')) return true
  try {
    const url = href.startsWith('//') ? new URL(`https:${href}`) : new URL(href)
    return /(^|\.)theitgirlguide\.com$/i.test(url.hostname)
  } catch {
    return true
  }
}

/** Drop stale ad click ids from an outbound href. Affiliate params stay. */
export function stripStaleTrackingHref(href: string) {
  const decoded = href.replace(/&amp;/gi, '&').replace(/&#0*38;/gi, '&')
  if (isInternalHref(decoded)) return href
  const cleaned = stripTrackingUrl(decoded)
  if (cleaned === decoded) return href
  return cleaned.replace(/&/g, '&amp;')
}

export function stripStaleTrackingHtml(html: string) {
  if (!html) return html
  return html.replace(/href=(["'])([^"']*)\1/gi, (full, quote: string, href: string) => {
    const next = stripStaleTrackingHref(href)
    return next === href ? full : `href=${quote}${next}${quote}`
  })
}

export function withSponsoredRel(html: string) {
  return html.replace(/<a\b([^>]*?)>/gi, (tag, attrs: string) => {
    const hrefMatch = attrs.match(/href\s*=\s*(?:"([^"]*)"|'([^']*)')/i)
    const href = hrefMatch?.[1] ?? hrefMatch?.[2] ?? ''
    const relMatch = attrs.match(/\srel\s*=\s*(?:"([^"]*)"|'([^']*)')/i)
    const existingRel = relMatch?.[1] ?? relMatch?.[2] ?? ''
    if (!isAffiliateHref(href) && !/\bsponsored\b/i.test(existingRel)) return tag
    const cleaned = attrs.replace(/\srel\s*=\s*(?:"[^"]*"|'[^']*')/i, '')
    return `<a${cleaned} rel="${AFFILIATE_REL}">`
  })
}
